import { useMemo } from 'react';

import { useAuth0SessionUser } from '@hooks/useAuth0SessionUser';
import { useEntitlements } from '@hooks/useEntitlements';
import { applyEntitlementsToUser } from '@lib/auth0/entitlements-user';

/**
 * The signed-in member, as the rest of the app should read them: the Auth0
 * session's identity claims, with the membership claims restated from
 * ffh-backend's entitlements (FFH-3019).
 *
 * Every consumer of the role helpers keeps its existing shape. What changes is
 * where roles and subscription come from: an Auth0 session now lasts months and
 * is never refreshed, so the claims it carries would otherwise be as old as the
 * login. Entitlements arrive a moment after the user does, so the first render
 * of a page still shows what the session claimed, then settles on the live
 * answer.
 *
 * @returns An object containing:
 *   - user: The authenticated user information typed as Auth0UserInfo, or undefined if not authenticated
 *   - error: Any error that occurred during authentication
 *   - isLoading: Boolean indicating whether the user data is currently being loaded
 */
export const useAuth0User = () => {
  const { user, error, isLoading } = useAuth0SessionUser();
  const { entitlements } = useEntitlements();

  // Memoised because consumers hold this object in effect dependencies: a new
  // identity on every render would re-run analytics identity effects with it.
  const resolvedUser = useMemo(
    () => applyEntitlementsToUser(user, entitlements),
    [user, entitlements]
  );

  return {
    user: resolvedUser,
    error,
    isLoading
  };
};
